import { useCallback, useMemo, useState } from 'react';

import { useDelayedAction } from './useDelayedAction';
import type { StepFlow, StepView } from '../types/step-flow-types';

/** How long a picked option stays visible before the flow moves on. */
export const ADVANCE_DELAY_MS = 500;

/**
 * Step navigation and picks for a list of items. Going back is always allowed;
 * going forward needs a pick on the current step. A pick moves to the next step
 * (or the review screen) after {@link ADVANCE_DELAY_MS}.
 */
export const useStepFlow = <TPick>(
  itemIds: ReadonlyArray<string>,
  /** A new key starts the flow over (first step, no picks). */
  resetKey = ''
): StepFlow<TPick> => {
  const total = itemIds.length;
  const [view, setView] = useState<StepView>({ kind: 'step', index: 0 });
  const [picks, setPicks] = useState<Record<string, TPick>>({});
  const { schedule, cancel } = useDelayedAction(ADVANCE_DELAY_MS);

  // Reset during render when the key changes (React's pattern for state that
  // belongs to a key), so a stale step is never painted for the new items.
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setView({ kind: 'step', index: 0 });
    setPicks({});
    cancel();
  }

  const currentIndex = view.kind === 'step' ? view.index : total;
  const currentId = view.kind === 'step' ? itemIds[view.index] ?? null : null;
  const currentPick = currentId !== null ? picks[currentId] ?? null : null;

  const showStep = useCallback(
    (index: number): StepView => (index >= total ? { kind: 'review' } : { kind: 'step', index }),
    [total]
  );

  const goBack = useCallback(() => {
    cancel();
    setView(showStep(Math.max(0, currentIndex - 1)));
  }, [cancel, currentIndex, showStep]);

  const goNext = useCallback(() => {
    if (!currentPick) return;
    cancel();
    setView(showStep(currentIndex + 1));
  }, [cancel, currentIndex, currentPick, showStep]);

  const select = useCallback(
    (pick: TPick) => {
      if (currentId === null) return;
      setPicks((prev) => ({ ...prev, [currentId]: pick }));
      schedule(() => setView(showStep(currentIndex + 1)));
    },
    [currentId, currentIndex, schedule, showStep]
  );

  return useMemo(
    () => ({
      view,
      currentId,
      currentPick,
      picks,
      canGoBack: currentIndex > 0,
      canGoNext: view.kind === 'step' && currentPick !== null,
      goBack,
      goNext,
      select,
    }),
    [view, currentId, currentPick, picks, currentIndex, goBack, goNext, select]
  );
};
